import cloneDeep from 'lodash/cloneDeep'
import min from 'lodash/min'
import max from 'lodash/max'
import sessionStorage from "../../common/session-storage"
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'

const EMPTY_ARRAY = []

const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1

export function getRectFromCoords(coords) {
  const allX = coords.map(coord => coord[0])
  const allY = coords.map(coord => coord[1])

  return {
    left: min(allX),
    top: min(allY),
    right: max(allX),
    bottom: max(allY)
  }
}

export function floatCoordinatesToStringPoint(coordinates) {
  return coordinates.join(',')
}

export function roundCoordinatePoint(coordinate) {
  return Math.round(coordinate * 100) / 100
}

export function pickX(coordinate) {
  return coordinate[0]
}

export function pickY(coordinate) {
  return coordinate[1]
}

export function concat(a, b) {
  return a.concat(b)
}

export function applyScale(scale) {
  return coordinate => coordinate * scale
}

export function applyTranslate(x, y) {
  return coordinate => {
    return [coordinate[0] + x, coordinate[1] + y]
  }
}

export function applyTranslatePoint(translateX, translateY) {
  return point => {
    return {
      x: point.x + translateX,
      y: point.y + translateY,
      width: point.width,
      opacity: point.opacity
    }
  }
}

export function translateYAxis(pageHeight, scale, y) {
  return pageHeight - scale * y
}

export function stringPointToFloatCoordinates(stringPoint) {
  return stringPoint.split(',').map(string => parseFloat(string))
}

export function translateAnnotation(annotation, translateX, translateY) {
  const translatedAnnotation = cloneDeep(annotation)

  if (annotation.rect) {
    const [[x1, y1], [x2, y2]] = annotation.rect
    const newRect = [
      [x1 + translateX, y1 + translateY],
      [x2 + translateX, y2 + translateY]
    ]
    translatedAnnotation.rect = newRect
  }

  if (annotation.coords) {
    translatedAnnotation.coords = translatedAnnotation.coords.map(path => {
      return path.map(point => {
        return [point[0] + translateX, point[1] + translateY]
      })
    })
  }

  if (annotation.inklist && annotation.inklist.gestures) {
    translatedAnnotation.inklist.gestures = annotation.inklist.gestures.map(
      path => {
        return path.map(point => {
          return {
            ...point,
            x: point.x + translateX,
            y: point.y + translateY
          }
        })
      }
    )
  }

  return translatedAnnotation
}

export function transformAnnotationSave(height, scale, annotation) {
  const transformedAnnotation = cloneDeep(annotation)
  if (annotation.rect) {
    transformedAnnotation.rect = transformedAnnotation.rect.map(point => {
      return [
        roundCoordinatePoint(point[0] / scale),
        roundCoordinatePoint(translateYAxis(height, 1, point[1]) / scale)
      ]
    })
  }
  if (annotation.coords) {
    transformedAnnotation.coords = transformedAnnotation.coords.map(path => {
      return path.map(point => {
        return [
          roundCoordinatePoint(point[0] / scale),
          roundCoordinatePoint(translateYAxis(height, 1, point[1]) / scale)
        ]
      })
    })
  }
  if (annotation.inklist && annotation.inklist.gestures) {
    transformedAnnotation.inklist.gestures = transformedAnnotation.inklist.gestures.map(
      path => {
        return path.map(point => {
          return {
            ...point,
            x: roundCoordinatePoint(point.x / scale),
            y: roundCoordinatePoint(translateYAxis(height, 1, point.y) / scale)
          }
        })
      }
    )
  }
  return transformedAnnotation
}

// translates Y axis and applies scale
// doc coordinate origin is bottom-left,
// SVG coordinate origin is top-left
export const transformAnnotationView = ({ height, scale }) => annotation => {
  const transformedAnnotation = cloneDeep(annotation)
  if (annotation.rect) {
    transformedAnnotation.rect = transformedAnnotation.rect.map(point => {
      return [point[0] * scale, translateYAxis(height, scale, point[1])]
    })
  }
  if (annotation.coords) {
    transformedAnnotation.coords = transformedAnnotation.coords.map(path => {
      return path.map(point => {
        return [point[0] * scale, translateYAxis(height, scale, point[1])]
      })
    })
  }
  if (annotation.inklist && annotation.inklist.gestures) {
    transformedAnnotation.inklist.gestures = transformedAnnotation.inklist.gestures.map(
      paths => {
        return paths.map(point => {
          return {
            ...point,
            x: point.x * scale,
            y: translateYAxis(height, scale, point.y)
          }
        })
      }
    )
  }
  return transformedAnnotation
}

export function getDimensions(annotation) {
  const gestures =
    (annotation.inklist && annotation.inklist.gestures) || EMPTY_ARRAY
  let left = null
  let top = null
  let right = null
  let bottom = null
  for (let gesture of gestures) {
    for (let coordinate of gesture || EMPTY_ARRAY) {
      if (left === null || coordinate[0] < left) {
        left = coordinate[0]
      }
      if (top === null || coordinate[1] < top) {
        top = coordinate[1]
      }
      if (right === null || coordinate[0] > right) {
        right = coordinate[0]
      }
      if (bottom === null || coordinate[1] > bottom) {
        bottom = coordinate[1]
      }
    }
  }
  return { left, top, right, bottom }
}

// create middle points for smoothing out changes
// in opacity and stroke width
export function multiplyPath(path) {
  const multipliedPath = []
  for (let i = 0; i < path.length; i += 1) {
    multipliedPath[i * 2] = path[i]
    if (path.length > i + 1) {
      const newPoint = Object.assign({}, path[i + 1])
      newPoint.x = (path[i].x + path[i + 1].x) / 2
      newPoint.y = (path[i].y + path[i + 1].y) / 2
      if (
        typeof path[i].opacity === 'number' &&
        typeof path[i + 1].opacity === 'number'
      ) {
        newPoint.opacity = (path[i].opacity + path[i + 1].opacity) / 2
      }
      if (
        typeof path[i].width === 'number' &&
        typeof path[i + 1].width === 'number'
      ) {
        newPoint.width = (path[i].width + path[i + 1].width) / 2
      }
      multipliedPath[i * 2 + 1] = newPoint
    }
  }
  return multipliedPath
}

// reducer
// TODO: reactor to be pure. don't mutate existing rect
export function horizontallyConnectRects(accumulator, current) {
  let conslidated = false
  accumulator.forEach(rect => {
    const isAdjacentToRight =
      rect.right >= current.left - 10 &&
      rect.left < current.left &&
      rect.right < current.right
    const isAdjacentToLeft =
      current.right >= rect.left - 10 &&
      current.left < rect.left &&
      current.right < rect.right
    const isVerticallyWithin =
      rect.top <= current.top && rect.bottom >= current.bottom
    if (isVerticallyWithin && isAdjacentToRight) {
      rect.right = current.right
      conslidated = true
    } else if (isVerticallyWithin && isAdjacentToLeft) {
      rect.left = current.left
      conslidated = true
    }
  })
  if (conslidated) {
    return accumulator
  } else {
    return accumulator.concat(current)
  }
}

// http://stackoverflow.com/a/306332/176758 (flipped Y-axis)
export function areRectanglesIntersecting(rectA, rectB) {
  return (
    rectA.left < rectB.right &&
    rectA.right > rectB.left &&
    rectA.top < rectB.bottom &&
    rectA.bottom > rectB.top
  )
}

// filter
export function intersectsWith(rectangleA) {
  return rectangleB => areRectanglesIntersecting(rectangleA, rectangleB)
}

// filter
export function intersectsWithSome(textSelectionRectangles) {
  return textDivRect => {
    return textSelectionRectangles.some(textSelectionRectangle =>
      areRectanglesIntersecting(textSelectionRectangle, textDivRect)
    )
  }
}

export function isInsideRectangle(rectA, rectB) {
  return (
    rectA.left >= rectB.left &&
    rectA.top >= rectB.top &&
    rectA.right <= rectB.right &&
    rectA.bottom <= rectB.bottom
  )
}

// reducer
// TODO: refactor to be pure. don't mutate existing rect
export function removeNeedlessRectOverlapping(accumulator, current) {
  if (!accumulator.length) {
    return [current]
  }
  const clonedCurrentRect = Object.assign({}, current)
  let modificationsMade
  let iterations = 0
  do {
    modificationsMade = false
    iterations++
    for (let rect of accumulator) {
      if (!areRectanglesIntersecting(rect, current)) {
        continue
      }
      if (isInsideRectangle(current, rect)) {
        return accumulator
      }

      const isRectWider = rect.right > current.right && rect.left < current.left
      const isRectThinner =
        rect.right < current.right && rect.left > current.left
      const isCurrentOverlappingAbove =
        current.top < rect.top &&
        current.bottom > rect.top &&
        current.bottom < rect.bottom
      const isCurrentOverlappingBelow =
        current.top > rect.top &&
        current.top < rect.bottom &&
        current.bottom > rect.bottom

      if (isCurrentOverlappingAbove) {
        if (isRectWider) {
          modificationsMade = true
          clonedCurrentRect.bottom = rect.top
        } else if (isRectThinner) {
          modificationsMade = true
          rect.top = clonedCurrentRect.bottom
        }
      } else if (isCurrentOverlappingBelow) {
        if (isRectWider) {
          modificationsMade = true
          clonedCurrentRect.top = rect.bottom
        } else if (isRectThinner) {
          modificationsMade = true
          rect.bottom = clonedCurrentRect.top
        }
      }
    }
  } while (modificationsMade && iterations < 100)
  if (iterations === 100) {
    console.warn('removeNeedlessClientRectOverlapping had too many iterations')
  }

  return accumulator.concat(clonedCurrentRect)
}

export function mergeRectangles(rectA, rectB) {
  const rectangles = []
  let left1
  let left2
  let right1
  let right2
  let rightmostRect
  let leftmostRect
  const highest = Math.min(rectA.top, rectB.top)
  const lowest = Math.max(rectA.bottom, rectB.bottom)
  if (rectA.left < rectB.left) {
    left1 = rectA.left
    left2 = rectB.left
    leftmostRect = rectA
  } else {
    left1 = rectB.left
    left2 = rectA.left
    leftmostRect = rectB
  }
  if (rectA.right < rectB.right) {
    right1 = rectA.right
    right2 = rectB.right
    rightmostRect = rectB
  } else {
    right1 = rectB.right
    right2 = rectA.right
    rightmostRect = rectA
  }
  if (left1 !== left2) {
    rectangles.push({
      left: left1,
      right: left2,
      top: leftmostRect.top,
      bottom: leftmostRect.bottom
    })
  }
  if (left2 !== right1) {
    rectangles.push({
      left: left2,
      right: right1,
      top: highest,
      bottom: lowest
    })
  }
  if (right1 !== right2) {
    rectangles.push({
      left: right1,
      right: right2,
      top: rightmostRect.top,
      bottom: rightmostRect.bottom
    })
  }
  return rectangles
}

export function getRectangleIntersection(rectA, rectB) {
  let leftEdge = Math.max(rectA.left, rectB.left)
  let topEdge = Math.max(rectA.top, rectB.top)
  let rightEdge = Math.min(rectA.right, rectB.right)
  let bottomEdge = Math.min(rectA.bottom, rectB.bottom)
  return {
    left: leftEdge,
    top: topEdge,
    right: rightEdge,
    bottom: bottomEdge
  }
}

// reducer
export function getRectangleIntersections(textLayerDivRects) {
  return (accumulator, textSelectionRectangle) => {
    return accumulator.concat(
      textLayerDivRects
        .filter(intersectsWith(textSelectionRectangle))
        .map(adjustedTextLayerDivRect => {
          return getRectangleIntersection(
            adjustedTextLayerDivRect,
            textSelectionRectangle
          )
        })
      // We can do this when redundancy problem solved
      // .reduce(mergeManyRectangles, [])
    )
  }
}

export function mergeManyRectangles(accumulator, rectangleA) {
  if (!accumulator.length) {
    return accumulator.concat(rectangleA)
  }
  let rectanglesToAdd = []
  accumulator.forEach(rectangleB => {
    rectanglesToAdd.push(...mergeRectangles(rectangleA, rectangleB))
  })
  return accumulator.concat(rectanglesToAdd)
}

export function withoutEmptyRects(rect) {
  return rect.top !== rect.bottom && rect.left !== rect.right
}

export function getRelatedTargetOrEquivalent(event) {
  return (
    event.explicitOriginalTarget ||
    event.relatedTarget ||
    event.rangeParent ||
    event.srcElement ||
    document.activeElement
  )
}

export function getTranslatedCordinates({
  pageX,
  pageY,
  originalPageX,
  originalPageY,
  viewportHeight,
  viewportWidth,
  x,
  y,
  height,
  width
}) {
  // Elsewhere: "We could just use transform="translate(0 0)", but using
  // a matrix is more versatile. The matrix is currently set at (1 0 0 1 0 0)"
  const newX = x + (pageX - originalPageX)
  const newY = y + (pageY - originalPageY)
  const translateX =
    newX < 0
      ? -x
      : newX > viewportWidth - width
      ? viewportWidth - width - x
      : pageX - originalPageX
  const translateY =
    newY < 0
      ? -y
      : newY > viewportHeight - height
      ? viewportHeight - height - y
      : pageY - originalPageY
  return {
    translateY,
    translateX
  }
}

export function annotationHasComment(commentReplies) {
  return (
    !!commentReplies &&
    typeof commentReplies == 'object' &&
    commentReplies.length > 0
  )
}

export function getCommentCreateButtonVisibility(
  hasComment,
  isCommentable,
  isCommenting,
  isSelected,
  annotationType,
  isDeleted
) {
  return (
    annotationType !== 'freetext' &&
    hasComment === false &&
    isCommentable &&
    isCommenting === false &&
    isSelected &&
    !isDeleted
  )
}

export function getCommentLineVisibility(
  isDragging,
  hasCommentCreateButton,
  isCommenting,
  hasComment,
  isHovered,
  isSelected
) {
  return (
    isDragging === false && // don't show lines during dragging until quirks solved
    (isCommenting ||
      hasCommentCreateButton ||
      (hasComment && (isHovered || isSelected)))
  )
}

export function scrollIntoView(node) {
  scrollIntoViewIfNeeded(node, { behavior: 'smooth' })
}

export const getMaxOffset = (
  managedCommentGroupTops,
  annotationsWithCommentGroups
) =>
  Math.max(
    0,
    ...annotationsWithCommentGroups.map(
      annotation =>
        managedCommentGroupTops[annotation.id] - annotation.rect[1][1]
    )
  )

export function focusOnNextAnnotation(
  ySortedStagedRootAnnotations,
  currentAnnotationId
) {
  const nextAnnotationIndex =
    ySortedStagedRootAnnotations.findIndex(
      annotation => currentAnnotationId === annotation.id
    ) + 1

  const hasNextAnnotation =
    nextAnnotationIndex < ySortedStagedRootAnnotations.length

  if (hasNextAnnotation) {
    const nextAnnotationId =
      ySortedStagedRootAnnotations[nextAnnotationIndex].id
    const annotation = document.querySelector(`#Annotation-${nextAnnotationId}`)
    const isFreetextAnnotation = annotation.classList.contains('FreetextGroup')
    if (isFreetextAnnotation) {
      annotation.querySelector('textarea').focus()
    } else {
      annotation.querySelector(`.Annotation-svg-container`).focus()
    }
  }
}

export const saveCommentFn = ({
  annotation,
  commentReplies,
  onUpdateAnnotation,
  viewportHeight,
  viewportScale,
  onCreateAnnotation,
  sessionUserObject
}) => ({ annotationId, annotationPayload, inreplyto }) => {
  const baseAnnotation = annotation
  const annotationComments = [...commentReplies, baseAnnotation]
  const rootAnnotation = annotationComments.find(
    annotation => annotation.id === annotationId
  )
  const contents = annotationPayload.contents

  if (rootAnnotation) {
    return onUpdateAnnotation(viewportHeight, viewportScale, {
      ...rootAnnotation,
      ...annotationPayload
    })
  } else {
    return onCreateAnnotation(viewportHeight, viewportScale, {
      id: annotationId,
      subject: 'Comment',
      type: 'commentReply',
      page: baseAnnotation.page,
      context: baseAnnotation.context,
      ctx_and_id: baseAnnotation.ctx_and_id,
      document_id: baseAnnotation.document_id,
      user_id: sessionUserObject.user_id,
      user_name: sessionUserObject.user_name,
      inreplyto,
      contents
    })
  }
}

export const deleteAnnotationFromSession = (accessToken, annotationId) => {
  const annotations = sessionStorage.annotations
  if (accessToken && annotations) {
    const parsedAnnotations = JSON.parse(annotations)
    if (parsedAnnotations[accessToken]) {
      parsedAnnotations[accessToken] = parsedAnnotations[accessToken].filter(ann => ann.id !== annotationId)
    }
    sessionStorage.setItem('annotations', JSON.stringify(parsedAnnotations))
  }
}
